import { html, nothing } from "lit";

export type CanvasViewProps = {
  gatewayUrl: string;
};

/**
 * Renders the Canvas admin view — an embedded iframe showing the Canvas Host
 * content and a toolbar for navigating between the HTML canvas and the
 * declarative UI renderer.
 */
export function renderCanvasView(props: CanvasViewProps) {
  const base = props.gatewayUrl.replace(/\/+$/, "");
  const canvasUrl = `${base}/__igloobot__/canvas/`;
  const rendererUrl = `${base}/__igloobot__/canvas-ui/`;

  return html`
    <style>
      .canvas-admin {
        display: flex;
        flex-direction: column;
        gap: 0;
        height: calc(100vh - 140px);
        min-height: 400px;
      }
      .canvas-toolbar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: var(--bg-secondary, #18181b);
        border: 1px solid var(--border, #27272a);
        border-radius: 8px 8px 0 0;
      }
      .canvas-toolbar button {
        appearance: none;
        border: 1px solid var(--border, #27272a);
        background: transparent;
        color: var(--text-secondary, #a1a1aa);
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
      }
      .canvas-toolbar button:hover {
        background: var(--bg-hover, rgba(255, 255, 255, 0.06));
        color: var(--text-primary, #e4e4e7);
      }
      .canvas-toolbar button.active {
        background: var(--accent, #3b82f6);
        color: #fff;
        border-color: var(--accent, #3b82f6);
      }
      .canvas-toolbar .spacer {
        flex: 1;
      }
      .canvas-toolbar .url-label {
        font-size: 12px;
        color: var(--text-muted, #71717a);
        font-family: ui-monospace, monospace;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 400px;
      }
      .canvas-frame {
        flex: 1;
        border: 1px solid var(--border, #27272a);
        border-top: none;
        border-radius: 0 0 8px 8px;
        overflow: hidden;
      }
      .canvas-frame iframe {
        width: 100%;
        height: 100%;
        border: none;
        background: #0a0a0f;
      }
    </style>

    <div class="canvas-admin">
      <div class="canvas-toolbar">
        <button
          id="canvas-tab-html"
          @click=${(e: Event) => {
            const frame = (e.target as HTMLElement)
              .closest(".canvas-admin")
              ?.querySelector("iframe") as HTMLIFrameElement | null;
            if (frame) {
              frame.src = canvasUrl;
            }
            const toolbar = (e.target as HTMLElement).closest(".canvas-toolbar");
            toolbar?.querySelectorAll("button").forEach((b) => b.classList.remove("active"));
            (e.target as HTMLElement).classList.add("active");
            const urlLabel = toolbar?.querySelector(".url-label");
            if (urlLabel) {
              urlLabel.textContent = canvasUrl;
            }
          }}
        >
          HTML Canvas
        </button>
        <button
          class="active"
          id="canvas-tab-ui"
          @click=${(e: Event) => {
            const frame = (e.target as HTMLElement)
              .closest(".canvas-admin")
              ?.querySelector("iframe") as HTMLIFrameElement | null;
            if (frame) {
              frame.src = rendererUrl;
            }
            const toolbar = (e.target as HTMLElement).closest(".canvas-toolbar");
            toolbar?.querySelectorAll("button").forEach((b) => b.classList.remove("active"));
            (e.target as HTMLElement).classList.add("active");
            const urlLabel = toolbar?.querySelector(".url-label");
            if (urlLabel) {
              urlLabel.textContent = rendererUrl;
            }
          }}
        >
          UI Renderer
        </button>
        <span class="spacer"></span>
        <span class="url-label">${rendererUrl}</span>
        <button
          @click=${() => {
            const frame = document.querySelector(
              ".canvas-frame iframe",
            ) as HTMLIFrameElement | null;
            if (frame) {
              frame.src = frame.src;
            }
          }}
        >
          Refresh
        </button>
        <button
          @click=${() => {
            const frame = document.querySelector(
              ".canvas-frame iframe",
            ) as HTMLIFrameElement | null;
            if (frame?.src) {
              window.open(frame.src, "_blank");
            }
          }}
        >
          Open
        </button>
      </div>
      <div class="canvas-frame">
        <iframe src=${rendererUrl} title="i-GlooBot Canvas"></iframe>
      </div>
    </div>
  `;
}
